import { Suspense, type ReactNode } from 'react';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { EllipsisLoading } from './components/loadings';
import { Box } from '@chakra-ui/react';

import RequireAuth from './components/RequireAuth';
import { lazyWithRetry } from 'src/modules/lazy-with-retry';
import { SentryBoundary } from 'src/modules/sentry-boundary';

const AuthPage = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "AuthPage" */
			'./Pages/Auth'
		)
);

const PasswordRecover = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "PasswordRecoverPage" */
			'./Pages/PasswordRecoverNew'
		)
);

const EventPos = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "EventPosPage" */
			'./Pages/EventPos'
		)
);

const EventManagementRoutes = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "EventManagementRoutesWrapper" */
			'src/Pages/EventManagement/Routes'
		)
);

const LandingReferral = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "LandingReferralPage" */
			'./Pages/LandingReferral'
		)
);

const CatchAllRedirect = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "CatchAllRedirect" */
			'./Pages/Event/CatchAllRedirect'
		)
);

const ExternalRedirect = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ExternalRedirect" */
			'./components/ExternalRedirect'
		)
);

const MeEvent = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "MeEventPage" */
			'./Pages/Me/Event'
		)
);

const MeEvents = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "MeEventsPage" */
			'./Pages/Me/Events'
		)
);

const MeEventsStart = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "MeEventsStartPage" */
      './Pages/Me/Events/Start'
    ),
);

const EventsCoupon = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "EventsCouponPage" */
			'./Pages/EventsCoupon'
		)
);

const MeAffiliations = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "MeAffiliationsPage" */
			'./Pages/Me/Affiliations'
		)
);

const MeAccount = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "MeAccountPage" */
			'./Pages/Me/PersonalData'
		)
);

const Confirm = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ConfirmPage" */
			'./Pages/Confirm'
		)
);

const PromoterNew = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "PromoterNewPage" */
			'./Pages/Promoter/New'
		)
);

const BillSection = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "BillSectionPage" */
			'./Pages/Me/Panel/Sections/BillSection'
		)
);

const LandingPage = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "LandingPage" */
			'./Pages/Landing'
		)
);

const RoutesWithHeader = lazyWithRetry(() =>
	import(
		/* webpackChunkName: "RoutesWithHeader" */
		'src/Layouts/WithHeader'
	).then(module => ({ default: module.RoutesWithHeader }))
);

const FinancialDashboard = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "FinancialDashboard" */
			'src/Pages/FinancialDashboard'
		)
);

const MeTickets = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "MeTicketsPage" */
			'./Pages/Me/Tickets'
		)
);

const PromoterSendTickets = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "PromoterSendTickets" */
			'./Pages/Promoter/SendTickets'
		)
);

const PromoterSoldTickets = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "PromoterSoldTickets" */
			'./Pages/Promoter/SoldTickets'
		)
);

const ColaboratorRoute = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ColaboratorPage" */
			'src/Pages/Colaborator'
		)
);

const Home = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "HomePage" */
			'src/Pages/Home'
		)
);

const HelpPageRoutes = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "HelpPage" */
			'src/Pages/HelpPage/Routes'
		)
);

const EventNew = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "CreateNewEvent" */
			'./Pages/Event'
		)
);

const EventRedirect = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "EventRedirect" */
			'./Pages/Event/EventRedirect'
		)
);

const ProducerProfile = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ProducerProfile" */
			'src/Pages/Producer/Profile'
		)
);

const ProducerNew = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ProducerNew" */
			'src/Pages/Producer/New/index'
		)
);

const ProducerEdit = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ProducerEdit" */
			'src/Pages/Producer/Edit'
		)
);

const Privacy = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "PrivacyPage" */
			'src/Pages/Docs/Privacy'
		)
);

const Terms = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "TermsPage" */
			'src/Pages/Docs/Terms'
		)
);

const ReferralRegulation = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "ReferralRegulationPage" */
			'src/Pages/Docs/ReferralRegulation'
		)
);

const InputsPlayground =
	process.env.NODE_ENV === 'development'
		? lazyWithRetry(
				() =>
					import(
						/* webpackChunkName: "InputsPlayground" */
						'src/Pages/Inputs'
					)
			)
		: null;

const LoadingPage = () => (
	<Box position="fixed" top="50%" left="50%" transform="translate(-50%,-50%)">
		<EllipsisLoading color="brand.500" />
	</Box>
);

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const RequireAuthExceptNewEvent = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const isNewEventRoute = location.pathname.startsWith('/me/events/new');

	if (isNewEventRoute) {
		return <>{children}</>;
	}

	return <RequireAuth>{children}</RequireAuth>;
};

export const AppRoutes = () => {
	return (
		<SentryRoutes>
			{InputsPlayground && (
				<Route
					path="/inputs"
					element={
						<Suspense fallback={<LoadingPage />}>
							<InputsPlayground />
						</Suspense>
					}
				/>
			)}

			<Route
				path="/ajuda/*"
				element={
					<Suspense
						fallback={
							<Box
								position="fixed"
								top="50%"
								left="50%"
								transform="translate(-50%,-50%)">
								<EllipsisLoading color="brand.500" />
							</Box>
						}>
						<HelpPageRoutes />
					</Suspense>
				}
			/>

			<Route
				path="/:userLevel/event/:eventID/*"
				element={
					<RequireAuth>
						<Suspense fallback={<LoadingPage />}>
							<EventManagementRoutes />
						</Suspense>
					</RequireAuth>
				}
			/>

			<Route
				path="/password-update/:email/:code"
				element={
					<Suspense
						fallback={
							<Box
								position="fixed"
								top="50%"
								left="50%"
								transform="translate(-50%,-50%)">
								<EllipsisLoading color="brand.500" />
							</Box>
						}>
						<PasswordRecover />
					</Suspense>
				}
			/>

			<Route
				path="/solucoes"
				element={
					<Suspense
						fallback={
							<Box
								position="fixed"
								top="50%"
								left="50%"
								transform="translate(-50%,-50%)">
								<EllipsisLoading color="brand.500" />
							</Box>
						}>
						<LandingPage />
					</Suspense>
				}
			/>

			<Route
				path="/indicacao"
				element={
					<Suspense fallback={<LoadingPage />}>
						<LandingReferral />
					</Suspense>
				}
			/>

			{/* Criar / Editar eventos */}
			<Route
				path="me/events/:id/*"
				element={
					<RequireAuthExceptNewEvent>
						<SentryBoundary flow="event_creation">
							<Suspense fallback={<LoadingPage />}>
								<MeEvent />
							</Suspense>
						</SentryBoundary>
					</RequireAuthExceptNewEvent>
				}
			/>

			{/* ROUTES WITH HEADER */}
			<Route
				path="/"
				element={
					<Suspense fallback={<LoadingPage />}>
						<RoutesWithHeader />
					</Suspense>
				}>
				<Route
					path="login"
					element={
						<SentryBoundary flow="auth">
							<Suspense fallback={<LoadingPage />}>
								<AuthPage />
							</Suspense>
						</SentryBoundary>
					}
				/>

				<Route
					path="cadastro"
					element={
						<SentryBoundary flow="auth">
							<Suspense fallback={<LoadingPage />}>
								<AuthPage initialSection="signup" />
							</Suspense>
						</SentryBoundary>
					}
				/>

				<Route
					path="evento/:eventSlug/:eventID/*"
					element={
						<SentryBoundary flow="event_page">
							<Suspense fallback={<LoadingPage />}>
								<EventNew />
							</Suspense>
						</SentryBoundary>
					}
				/>

				<Route
					path="event/:eventID/*"
					element={
						<Suspense fallback={<LoadingPage />}>
							<EventRedirect />
						</Suspense>
					}
				/>

				<Route
					path="event-pos/:eventID"
					element={
						<Suspense fallback={<LoadingPage />}>
							<EventPos />
						</Suspense>
					}
				/>

				<Route
					path="me/account"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<MeAccount />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="me/affiliations"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<MeAffiliations />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="me/events"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<MeEvents />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="me/events/start"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<MeEventsStart />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="me/tickets"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<MeTickets />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="painel/faturas"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<BillSection />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="terms"
					element={
						<Suspense fallback={<LoadingPage />}>
							<Terms />
						</Suspense>
					}
				/>

				<Route
					path="regulamento_programa_indicacao"
					element={
						<Suspense fallback={<LoadingPage />}>
							<ReferralRegulation />
						</Suspense>
					}
				/>

				<Route
					path="privacy"
					element={
						<Suspense fallback={<LoadingPage />}>
							<Privacy />
						</Suspense>
					}
				/>

				<Route
					path="confirm/:token"
					element={
						<Suspense fallback={<LoadingPage />}>
							<Confirm />
						</Suspense>
					}
				/>

				<Route
					path="newpromoter"
					element={
						<Suspense fallback={<LoadingPage />}>
							<PromoterNew />
						</Suspense>
					}
				/>

				<Route
					path="novocolaborador"
					element={
						<Suspense fallback={<LoadingPage />}>
							<ColaboratorRoute />
						</Suspense>
					}
				/>

				<Route
					path="organizador/novo"
					element={
						<Suspense fallback={<LoadingPage />}>
							<ProducerNew />
						</Suspense>
					}
				/>

				<Route
					path="organizador/editar"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<ProducerEdit />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="send-tickets/:eventID"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<PromoterSendTickets />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="ticket/:ticketGroupId"
					element={
						<Suspense fallback={<LoadingPage />}>
							<PromoterSoldTickets />
						</Suspense>
					}
				/>

				<Route
					path="coupon/:coupon/events/:userId"
					element={
						<Suspense fallback={<LoadingPage />}>
							<EventsCoupon />
						</Suspense>
					}
				/>

				<Route
					path="financeiro"
					element={
						<RequireAuth>
							<Suspense fallback={<LoadingPage />}>
								<FinancialDashboard />
							</Suspense>
						</RequireAuth>
					}
				/>

				<Route
					path="blog"
					element={
						<Suspense fallback={<LoadingPage />}>
							<ExternalRedirect to="https://uticket.com.br/blog" />
						</Suspense>
					}
				/>

				<Route
					index
					element={
						<Suspense fallback={<LoadingPage />}>
							<Home />
						</Suspense>
					}
				/>

				<Route
					path="*"
					element={
						<Suspense fallback={<LoadingPage />}>
							<CatchAllRedirect />
						</Suspense>
					}
				/>
			</Route>

			{/* TODAS AS ROTAS DEVEM SER ACIMA DESTA */}
			<Route
				path="/:profileSlug"
				element={
					<Suspense fallback={<LoadingPage />}>
						<ProducerProfile />
					</Suspense>
				}
			/>

			<Route
				path="*"
				element={
					<Suspense fallback={<LoadingPage />}>
						<CatchAllRedirect />
					</Suspense>
				}
			/>
		</SentryRoutes>
	);
};
